import { a as getCompoundVariantCss, c as splitProps, d as withDefaults, f as __exportAll, i as mergeCss, l as toVariantMap, o as memo, r as css, s as mergeProps, t as MenuGroupInternal, u as uniq } from "./menu-group-internal-usFoCH26.js";
import { Style } from "@tecnobit-srl/styles-manager";
import "@tecnobit-srl/content-types-menu-group/css";
import { MenuSectionContextProvider, useMenu, useMenuSection } from "@tecnobit-srl/menu";
import { Link } from "@tecnobit-srl/content-types-link/lazy";
import { Content } from "@tecnobit-srl/content-types-store";
import { useCustomAction } from "@tecnobit-srl/custom-actions";
import useClickOutside from "@tecnobit-srl/hook-use-click-outside";
import { useIsCurrentUrl } from "@tecnobit-srl/hook-use-is-current-url";
import { isValidContentArray } from "@tecnobit-srl/hook-use-valid-content";
import { useNavigateTo } from "@tecnobit-srl/router";
import { FontAwesome } from "@tecnobit-srl/ui-font-awesome";
import { useTheme } from "@tecnobit-srl/ui-theme";
import { zIndexes } from "@tecnobit-srl/ui-zindexes";
import { map } from "lodash-es";
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@tecnobit-srl/content-types-badge/lazy";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
//#region ../../styled-system/styled-system/css/cva.js
var cva = (config) => {
	const defaults = (c) => ({
		base: {},
		variants: {},
		defaultVariants: {},
		compoundVariants: [],
		...c
	});
	const { base, variants, defaultVariants, compoundVariants } = defaults(config);
	const getVariantProps = (props) => withDefaults(defaultVariants, props);
	const resolve = (props = {}) => {
		const computed = getVariantProps(props);
		const styles = [base];
		for (const key in computed) {
			const value = computed[key];
			if (variants[key]?.[value]) styles.push(variants[key][value]);
		}
		styles.push(getCompoundVariantCss(compoundVariants, computed));
		return mergeCss(...styles);
	};
	const variantKeys = Object.keys(variants);
	const variantMap = toVariantMap(variants);
	const resolveMemo = memo(resolve);
	const merge = (other) => {
		const override = defaults(other.config);
		const keys = uniq(other.variantKeys, variantKeys);
		return cva({
			base: mergeCss(base, override.base),
			variants: Object.fromEntries(keys.map((key) => [key, mergeCss(variants[key], override.variants[key])])),
			defaultVariants: mergeProps(defaultVariants, override.defaultVariants),
			compoundVariants: [...compoundVariants, ...override.compoundVariants]
		});
	};
	return Object.assign(memo(function cvaFn(props) {
		return css(resolve(props));
	}), {
		__cva__: true,
		variantMap,
		variantKeys,
		raw: resolve,
		__memoizedRaw__: resolveMemo,
		config,
		merge,
		splitVariantProps(props) {
			return splitProps(props, variantKeys);
		},
		getVariantProps
	});
};
//#endregion
//#region ../../styled-system/styled-system/css/cx.js
/**
* Conditionally join classNames into a single string
*/
function cx(...args) {
	let str = "", i = 0, arg;
	for (; i < arguments.length;) if ((arg = arguments[i++]) && typeof arg === "string") {
		str && (str += " ");
		str += arg;
	}
	return str;
}
//#endregion
//#region src/menu-group-desktop.tsx
function MenuGroupDesktop({ uuid, title, href, icon, customAction, items, columns, badge }) {
	const theme = useTheme();
	const { viewport, section, area, subarea } = useMenuSection();
	const menuSectionValue = useMemo(() => ({
		viewport,
		section,
		area,
		subarea,
		childOf: "group"
	}), [
		area,
		section,
		subarea,
		viewport
	]);
	const customActionFn = useCustomAction(customAction);
	const navigate = useNavigateTo();
	const onClick = useCallback(async (e) => {
		if (customActionFn) {
			e.preventDefault();
			Promise.resolve(customActionFn()).catch();
		}
		if (href) await navigate(href);
		setOpen(true);
	}, [
		customActionFn,
		navigate,
		href
	]);
	const [open, setOpen] = useState(false);
	const groupRef = useRef(null);
	const isCurrentUrl = useIsCurrentUrl(href);
	useClickOutside(groupRef, () => {
		setOpen(false);
	});
	const { addHighlight, removeHighlight } = useMenu();
	useEffect(() => {
		if (open) addHighlight(uuid);
		else removeHighlight(uuid);
	}, [
		addHighlight,
		open,
		removeHighlight,
		uuid
	]);
	const groupStyle = useMemo(() => ({
		width: 300 * (columns ?? 1),
		zIndex: zIndexes.menuOverlay
	}), [columns]);
	return /* @__PURE__ */ jsxs("div", {
		ref: groupRef,
		className: cx("menu-group", css({ position: "relative" })),
		onMouseLeave: () => {
			setOpen(false);
		},
		onMouseEnter: () => {
			setOpen(true);
		},
		children: [/* @__PURE__ */ jsxs(Link, {
			href,
			title,
			onClick,
			css: cva({
				base: {
					color: theme === "dark" ? "white" : "text.500",
					px: "4",
					py: "2",
					mx: "-1",
					cursor: "pointer",
					textDecoration: "none",
					borderRadius: "full",
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "center",
					transition: "background-color .25s ease",
					...isCurrentUrl ? { backgroundColor: theme === "dark" ? "white/10" : "accent.100" } : {}
				},
				variants: {
					isCurrentUrl: {
						true: { ".menu-group:is(:hover, [data-hover]) &": { backgroundColor: theme === "dark" ? "white/20" : "accent.100" } },
						false: { ".menu-group:is(:hover, [data-hover]) &": { backgroundColor: theme === "dark" ? "white/15" : "accent.50" } }
					},
					section: {
						top: { fontSize: "sm" },
						bottom: {}
					}
				}
			}).raw({
				isCurrentUrl,
				section
			}),
			children: [
				icon ? /* @__PURE__ */ jsx(FontAwesome, {
					size: 1,
					css: css.raw({ mr: "2" }),
					name: icon
				}) : null,
				title,
				badge ? /* @__PURE__ */ jsx(Badge, {
					css: css.raw({ ml: "1" }),
					children: badge
				}) : null,
				/* @__PURE__ */ jsx(FontAwesome, {
					size: .75,
					css: css.raw({
						ml: "2",
						lineHeight: 0,
						rotate: open ? "180deg" : "0deg",
						transition: "rotate .1s ease, opacitys .1s ease",
						color: theme === "dark" ? "white" : "text.500",
						opacity: open ? 1 : .5
					}),
					name: "fa-solid fa-chevron-down"
				})
			]
		}), /* @__PURE__ */ jsx("div", {
			style: groupStyle,
			className: css({
				position: "absolute",
				top: "100%",
				left: 0,
				py: "2",
				backgroundColor: "white",
				rounded: "xl",
				shadow: "md",
				display: open ? "flex" : "none",
				".menu-group:is(:hover, [data-hover]) &": { display: "flex" },
				flexDir: "row",
				flexWrap: "wrap",
				alignItems: "start",
				justifyContent: "center",
				overflow: "hidden"
			}),
			children: /* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: menuSectionValue,
				children: isValidContentArray(items) ? map(items, (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid)) : items
			})
		})]
	});
}
//#endregion
//#region src/menu-group-footer.tsx
function MenuGroupFooter({ title, items }) {
	const theme = useTheme();
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			w: "full",
			display: "flex",
			flexDirection: "column",
			gap: 2
		}),
		children: [title ? /* @__PURE__ */ jsx("span", {
			className: css({
				fontSize: "lg",
				fontWeight: "bold",
				color: theme === "dark" ? "white" : "text.500"
			}),
			children: title
		}) : null, /* @__PURE__ */ jsx("div", {
			className: css({
				w: "full",
				display: "flex",
				flexDirection: "row",
				gap: 2
			}),
			children: isValidContentArray(items) ? map(items, (c) => /* @__PURE__ */ createElement(Content, {
				...c,
				key: c.uuid
			})) : items
		})]
	});
}
//#endregion
//#region src/menu-group.tsx
var menu_group_exports = /* @__PURE__ */ __exportAll({ MenuGroup: () => MenuGroup });
function MenuGroup(props) {
	const { viewport, section } = useMenuSection();
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Style, { style: "core-content-types/menu-group" }),
		section !== "footer" && viewport === "desktop" && ["top", "bottom"].includes(section) && /* @__PURE__ */ jsx(MenuGroupDesktop, { ...props }),
		section !== "footer" && viewport === "mobile" && /* @__PURE__ */ jsx(MenuGroupInternal, { ...props }),
		section === "footer" && /* @__PURE__ */ jsx(MenuGroupFooter, { ...props })
	] });
}
//#endregion
export { menu_group_exports as n, MenuGroup as t };
